// src/pages/FinanceReportPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import { API_BASE_URL } from "../config/constants";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  red: "#dc2626",
};

const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const FinanceReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useState(defaultFrom());
  const [toDate, setToDate] = useState(today());
  const [summary, setSummary] = useState({ expense: 0, income: 0, net: 0 });
  const [byCategory, setByCategory] = useState({ expense: [], income: [] });
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/finance/profit-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          from_date: fromDate,
          to_date: toDate,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load report");
      const apiSummary = json?.summary || {};
      const sales = Number(apiSummary.total_sales || 0);
      const otherIncome = Number(apiSummary.total_other_income || 0);
      const expense = Number(apiSummary.total_expense || 0);
      const income = Number(apiSummary.total_revenue ?? sales + otherIncome);
      const incomeCategories = Array.isArray(json?.by_category?.income)
        ? json.by_category.income
        : [];

      setSummary({
        expense,
        income,
        net: Number(apiSummary.net_profit ?? income - expense),
      });
      setByCategory({
        expense: Array.isArray(json?.by_category?.expense) ? json.by_category.expense : [],
        income: [
          ...(sales > 0 ? [{ category_name: "Sales", total_amount: sales }] : []),
          ...incomeCategories,
        ],
      });
      setDailyTrend(
        (Array.isArray(json?.daily_trend) ? json.daily_trend : []).map((row) => {
          const dailySales = Number(row.sales || 0);
          const dailyOtherIncome = Number(row.income ?? row.other_income ?? 0);
          const dailyExpense = Number(row.expense || 0);
          const dailyIncome = dailySales + dailyOtherIncome;
          return {
            date: row.date,
            expense: dailyExpense,
            income: dailyIncome,
            net: Number(row.net_profit ?? dailyIncome - dailyExpense),
          };
        })
      );
    } catch (e) {
      toast.error(e.message || "Unable to load finance report");
      setSummary({ expense: 0, income: 0, net: 0 });
      setByCategory({ expense: [], income: [] });
      setDailyTrend([]);
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const groupedTrend = useMemo(() => {
    return [...dailyTrend].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [dailyTrend]);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader title="Finance Report" subtitle="Daily sales and other income compared with expenses." stats={[{ label: "Expense", value: fmtINR(summary.expense) }, { label: "Total Income", value: fmtINR(summary.income) }, { label: "Net", value: fmtINR(summary.net) }]} />

      <ReportControlBar>
        <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Grid ref={reportRef}>
        <Card>
          <CardTitle>By Category — Expense</CardTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.expense?.map((c, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{c.category_name || c.name || "Uncategorized"}</td>
                    <td>{fmtINR(c.total_amount)}</td>
                  </tr>
                ))}
                {!byCategory.expense?.length && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", padding: 12, color: COLOR.muted }}>
                      No expense categories
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>

        <Card>
          <CardTitle>By Category — Income (Sales + Other Income)</CardTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.income?.map((c, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{c.category_name || c.name || "Uncategorized"}</td>
                    <td>{fmtINR(c.total_amount)}</td>
                  </tr>
                ))}
                {!byCategory.income?.length && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", padding: 12, color: COLOR.muted }}>
                      No income categories
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      </Grid>

      <Card>
        <CardTitle>Daily Trend</CardTitle>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Expense</th>
                <th>Income</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {groupedTrend.map((d) => {
                const net = Number(d.net ?? (Number(d.income || 0) - Number(d.expense || 0)));
                return (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    <td>{fmtINR(d.expense)}</td>
                    <td>{fmtINR(d.income)}</td>
                    <td style={{ color: net >= 0 ? COLOR.green : COLOR.red, fontWeight: 800 }}>
                      {fmtINR(net)}
                    </td>
                  </tr>
                );
              })}
              {!groupedTrend.length && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: 12, color: COLOR.muted }}>
                    No entries for this range.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
      <Actions style={{ marginTop: 10 }}>
        <PrimaryBtn
          onClick={() => downloadPDF(reportRef.current, `finance_report_${fromDate}_to_${toDate}.pdf`)}
        >
          <FaSyncAlt /> PDF
        </PrimaryBtn>
        <PrimaryBtn
          onClick={() => printThermal(reportRef.current, `finance_report_${fromDate}_to_${toDate}`)}
        >
          Thermal Print
        </PrimaryBtn>
        <PrimaryBtn
          onClick={() => {
            const headers = ["Date", "Expense", "Income", "Net"];
            const rows = groupedTrend.map((d) => [
              d.date,
              d.expense,
              d.income,
              Number(d.income || 0) - Number(d.expense || 0),
            ]);
            downloadExcel(`finance_report_${fromDate}_to_${toDate}.xlsx`, headers, rows);
          }}
        >
          Excel
        </PrimaryBtn>
        <PrimaryBtn
          onClick={() => {
            const headers = ["Date", "Expense", "Income", "Net"];
            const rows = groupedTrend.map((d) => [
              d.date,
              d.expense,
              d.income,
              Number(d.income || 0) - Number(d.expense || 0),
            ]);
            downloadCSV(`finance_report_${fromDate}_to_${toDate}.csv`, headers, rows);
          }}
        >
          <FaSyncAlt /> CSV
        </PrimaryBtn>
      </Actions>
    </Page>
  );
};

export default FinanceReportPage;

const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatYMD(d);
};
const formatYMD = (d) => {
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom:16px;padding:26px 28px;border-radius:22px;background:linear-gradient(135deg,#075f9e,#0787c5);box-shadow:0 14px 30px rgba(1,91,154,.17);
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color:#fff;
`;
const Sub = styled.div`
  color:#dff3ff;
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: rgba(255,255,255,.14);
  color: #fff;
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border:1px solid rgba(255,255,255,.25);
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const CardTitle = styled.h3`
  margin: 0 0 10px;
  color: ${COLOR.text};
`;
const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  thead {
    background: #eef2ff;
  }
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 16px;
`;
